/**
 * Filtering and data aggregation utilities for the Analytics page
 */

import type {
  WorkerAssignment,
  WorkerAssignmentLog,
  Zone,
  Location,
} from 'src/store/todo/types'
import type { WorkerZoneAssignment, GridLayout, WorkerValueSummaryData } from '../types'
import { formatTime } from './formatting'

/**
 * Group workers by zone from assignments
 * Creates one entry per worker+task combination per zone
 *
 * @param assignments - Array of worker assignments
 * @param logs - Array of worker assignment logs
 * @param selectedTeamId - ID of the selected team
 * @param selectedTasks - Array of selected task IDs (for filtering)
 * @returns Record mapping zone IDs to arrays of worker zone assignments
 */
export const groupWorkersByZone = (
  assignments: WorkerAssignment[],
  logs: WorkerAssignmentLog[],
  selectedTeamId: string | null,
  selectedTasks: string[]
): Record<string, WorkerZoneAssignment[]> => {
  const workersByZone: Record<string, WorkerZoneAssignment[]> = {}

  assignments.forEach((assignment) => {
    if (
      assignment.teamId === selectedTeamId &&
      !assignment.softDeleted &&
      (!selectedTasks.length || selectedTasks.includes(assignment.taskTypeId))
    ) {
      if (!workersByZone[assignment.zoneId]) {
        workersByZone[assignment.zoneId] = []
      }

      // Check if this worker+task combination already exists for this zone
      const existingEntry = workersByZone[assignment.zoneId].find(
        (e) => e.workerId === assignment.workerId && e.taskTypeId === assignment.taskTypeId
      )

      if (existingEntry) {
        // Increment location count and completed count if applicable
        if (assignment.locationId) {
          existingEntry.locationCount += 1
          // Check for completion log
          const hasCompletionLog = logs.some(
            (log) =>
              log.workerId === assignment.workerId &&
              log.taskTypeId === assignment.taskTypeId &&
              log.zoneId === assignment.zoneId &&
              log.locationId === assignment.locationId &&
              !log.softDeleted
          )
          if (hasCompletionLog) {
            existingEntry.completedLocationCount += 1
          }
        } else {
          // Zone-level assignment - update counts based on completion logs
          existingEntry.hasZoneLevelAssignment = true

          // Count completed locations from logs for this zone assignment
          const uniqueCompletedLocations = new Set(
            logs
              .filter(
                (log) =>
                  log.workerId === assignment.workerId &&
                  log.taskTypeId === assignment.taskTypeId &&
                  log.zoneId === assignment.zoneId &&
                  log.locationId && // Has a locationId (location completion)
                  !log.softDeleted
              )
              .map((log) => log.locationId)
          )
          existingEntry.completedLocationCount = uniqueCompletedLocations.size
        }
      } else {
        // For location-specific assignments, check for completion log
        const hasCompletionLog = assignment.locationId
          ? logs.some(
              (log) =>
                log.workerId === assignment.workerId &&
                log.taskTypeId === assignment.taskTypeId &&
                log.zoneId === assignment.zoneId &&
                log.locationId === assignment.locationId &&
                !log.softDeleted
            )
          : false

        // For zone-level assignments, count completed locations from logs
        let zoneCompletedCount = 0
        if (!assignment.locationId) {
          const uniqueCompletedLocations = new Set(
            logs
              .filter(
                (log) =>
                  log.workerId === assignment.workerId &&
                  log.taskTypeId === assignment.taskTypeId &&
                  log.zoneId === assignment.zoneId &&
                  log.locationId &&
                  !log.softDeleted
              )
              .map((log) => log.locationId)
          )
          zoneCompletedCount = uniqueCompletedLocations.size
        }

        // New entry for this worker+task in this zone
        workersByZone[assignment.zoneId].push({
          workerId: assignment.workerId,
          taskTypeId: assignment.taskTypeId,
          locationCount: assignment.locationId ? 1 : 0,
          completedLocationCount: assignment.locationId
            ? hasCompletionLog
              ? 1
              : 0
            : zoneCompletedCount,
          hasZoneLevelAssignment: !assignment.locationId,
        })
      }
    }
  })

  return workersByZone
}

/**
 * Build a proper grid layout: rows x columns with contiguous positions
 *
 * @param zones - Array of zones with position data
 * @returns Grid layout structure with 2D array and row indices
 */
export const buildGridLayout = (zones: Zone[]): GridLayout => {
  // Find the dimensions
  let maxRow = 0
  let maxColumn = 0
  const zoneMap: Record<string, { id: string; zoneMoniker: string }> = {}

  zones.forEach((zone) => {
    const row = zone.widgetZoneRow ?? 0
    const col = zone.widgetZoneColumn ?? 0
    maxRow = Math.max(maxRow, row)
    maxColumn = Math.max(maxColumn, col)
    zoneMap[`${col}-${row}`] = {
      id: zone.id,
      zoneMoniker: zone.zoneMoniker,
    }
  })

  // Create grid array: grid[column][row] = zone
  const fullGrid: Array<Array<{ id: string; zoneMoniker: string } | null>> = []
  for (let col = 0; col <= maxColumn; col++) {
    fullGrid[col] = []
    for (let row = 0; row <= maxRow; row++) {
      fullGrid[col][row] = zoneMap[`${col}-${row}`] || null
    }
  }

  // Filter out completely empty columns
  const filteredGrid = fullGrid.filter((column) => column.some((zone) => zone !== null))

  // Find which rows have at least one zone
  const rowsWithZones = new Set<number>()
  zones.forEach((zone) => {
    const row = zone.widgetZoneRow ?? 0
    rowsWithZones.add(row)
  })
  const sortedRows = Array.from(rowsWithZones).sort((a, b) => a - b)

  return { grid: filteredGrid, rowIndices: sortedRows }
}

/**
 * Get worker value summary data across all zones and locations
 * Returns all individual records sorted by timestamp (most recent first)
 *
 * @param logs - Array of all worker assignment logs
 * @param zones - Array of zones
 * @param locations - Array of locations
 * @param workerId - ID of the worker
 * @param taskTypeId - ID of the task type (optional - if not provided, shows all tasks)
 * @returns Array of summary data records
 */
export const getWorkerValueSummary = (
  logs: WorkerAssignmentLog[],
  zones: Zone[],
  locations: Location[],
  workerId: string,
  taskTypeId?: string
): WorkerValueSummaryData[] => {
  const filteredLogs = logs.filter(
    (log) =>
      log.workerId === workerId &&
      (taskTypeId ? log.taskTypeId === taskTypeId : true) &&
      !log.softDeleted
  )

  // Sort by timestamp descending (most recent first)
  const sortedLogs = [...filteredLogs].sort((a, b) => b.timestamp - a.timestamp)

  // Convert to summary format with zone and location names
  const zoneMap: Record<string, Zone> = {}
  zones.forEach((z) => {
    zoneMap[z.id] = z
  })

  return sortedLogs.map((log) => {
    const zone = zoneMap[log.zoneId]
    const zoneName = zone?.zoneMoniker || log.zoneId
    const location = locations.find((l) => l.id === log.locationId)
    const locationName = location?.locationMoniker || location?.locationName

    return {
      id: log.id,
      zoneId: log.zoneId,
      zoneName,
      locationId: log.locationId,
      locationName,
      value: log.value,
      timestamp: log.timestamp,
      formattedTime: formatTime(log.timestamp),
    }
  })
}

/**
 * Get ALL worker harvest summary data across all zones, locations, and task types
 * Returns all individual harvest records sorted by timestamp (most recent first)
 *
 * @param logs - Array of all worker assignment logs
 * @param zones - Array of zones
 * @param locations - Array of locations
 * @param workerId - ID of the worker
 * @param harvestTaskIds - Array of harvest task type IDs
 * @returns Array of summary data records
 */
export const getWorkerAllHarvestsSummary = (
  logs: WorkerAssignmentLog[],
  zones: Zone[],
  locations: Location[],
  workerId: string,
  harvestTaskIds: string[]
): WorkerValueSummaryData[] => {
  const filteredLogs = logs.filter(
    (log) =>
      log.workerId === workerId &&
      harvestTaskIds.includes(log.taskTypeId) &&
      !log.softDeleted
  )

  // Sort by timestamp descending (most recent first)
  const sortedLogs = [...filteredLogs].sort((a, b) => b.timestamp - a.timestamp)

  // Convert to summary format with zone and location names
  const zoneMap: Record<string, Zone> = {}
  zones.forEach((z) => {
    zoneMap[z.id] = z
  })

  return sortedLogs.map((log) => {
    const zone = zoneMap[log.zoneId]
    const zoneName = zone?.zoneMoniker || log.zoneId
    const location = locations.find((l) => l.id === log.locationId)
    const locationName = location?.locationMoniker || location?.locationName

    return {
      id: log.id,
      zoneId: log.zoneId,
      zoneName,
      locationId: log.locationId,
      locationName,
      value: log.value,
      timestamp: log.timestamp,
      formattedTime: formatTime(log.timestamp),
    }
  })
}

/**
 * Get all assignments for a specific zone
 *
 * @param assignments - Array of all worker assignments
 * @param zoneId - ID of the zone
 * @param selectedTeamId - ID of the selected team
 * @returns Filtered array of assignments for the zone
 */
export const getZoneAssignments = (
  assignments: WorkerAssignment[],
  zoneId: string,
  selectedTeamId: string | null
): WorkerAssignment[] => {
  return assignments.filter(
    (a) => a.zoneId === zoneId && a.teamId === selectedTeamId && !a.softDeleted
  )
}
